import type {
  DeviceMetricAvailability,
  DeviceMetricCategory,
  DeviceMetricGroup,
  DeviceMetricsResponse,
  HubDeviceHealth,
  HubDeviceHealthSummary,
  MapDevice,
  MapHub,
  StatusAlert
} from './statusTelemetryApi';

export const DEVICE_METRIC_CATEGORIES: DeviceMetricCategory[] = [
  'resource',
  'environment',
  'hardware',
  'routing',
  'collection'
];

export type HubDeviceMarkerDatum = {
  kind: 'hub-device-health';
  id: string;
  hub: MapHub;
  position: [number, number, number];
  summary: HubDeviceHealthSummary;
  devices: MapDevice[];
  alerts: StatusAlert[];
};

export type HubDeviceViewModel = {
  id: string;
  displayName: string;
  hostname?: string;
  role?: string;
  reachability: string;
  collectionHealth: string;
  overlayExpected: number;
  overlayReady: number;
  freshness: string;
  alertCount: number;
  unacknowledgedAlertCount: number;
};

export type HubDeviceDrawerViewModel = {
  hubID: string;
  hubName: string;
  overall: HubDeviceHealth;
  summary: HubDeviceHealthSummary;
  devices: HubDeviceViewModel[];
  alerts: StatusAlert[];
};

export type DeviceMetricGroupViewModel = {
  category: DeviceMetricCategory;
  label: string;
  availability: DeviceMetricAvailability;
  observedAt?: string;
  metrics: DeviceMetricGroup['metrics'];
  returnedCount: number;
  totalCount: number;
  truncated: boolean;
  diagnostic?: string;
};

export type DeviceMetricsViewModel = {
  deviceID: string;
  displayName: string;
  hostname?: string;
  role?: string;
  reachability: string;
  collectionHealth: string;
  freshness: string;
  alertCount: number;
  alertsTruncated: boolean;
  groups: DeviceMetricGroupViewModel[];
};

const belongsToHub = (device: MapDevice, hub: MapHub) => device.site_id === hub.id;

const alertsForHub = (
  hub: MapHub,
  devices: MapDevice[],
  activeAlerts: StatusAlert[]
): StatusAlert[] => {
  const resourceIDs = new Set([
    hub.id,
    hub.external_id,
    ...devices.map(device => device.id),
    ...hub.alert_summary.alert_ids,
    ...devices.flatMap(device => device.alert_summary.alert_ids)
  ].filter((id): id is string => Boolean(id)));
  return activeAlerts
    .filter(alert => resourceIDs.has(alert.alert_id) || Boolean(alert.resource_id && resourceIDs.has(alert.resource_id)))
    .sort((left, right) => {
      const rank = (alert: StatusAlert) =>
        alert.severity === 'critical' ? 0 : alert.severity === 'warning' ? 1 : 2;
      return rank(left) - rank(right) || left.opened_at.localeCompare(right.opened_at);
    });
};

export const projectHubDeviceMarkers = (
  hubs: MapHub[],
  devices: MapDevice[],
  activeAlerts: StatusAlert[]
): HubDeviceMarkerDatum[] =>
  hubs.flatMap(hub => {
    const summary = hub.device_health_summary;
    if (!hub.location || !summary || summary.monitored_count < 1) return [];
    const hubDevices = devices.filter(device => device.monitoring_enabled && belongsToHub(device, hub));
    return [{
      kind: 'hub-device-health',
      id: hub.id,
      hub,
      position: [hub.location.lng, hub.location.lat, 0],
      summary,
      devices: hubDevices,
      alerts: alertsForHub(hub, hubDevices, activeAlerts)
    }];
  });

const DEVICE_STATE_RANK: Record<string, number> = {
  unreachable: 0,
  down: 0,
  critical: 0,
  degraded: 1,
  failed: 1,
  stale: 2,
  unavailable: 2,
  unknown: 2,
  healthy: 3,
  reachable: 3,
  up: 3,
  fresh: 3
};

const freshnessTime = (device: MapDevice) =>
  Date.parse(device.freshness.received_at || device.freshness.observed_at || '') || 0;

const deviceRank = (device: MapDevice) =>
  Math.min(
    DEVICE_STATE_RANK[device.reachability.toLowerCase()] ?? 2,
    DEVICE_STATE_RANK[device.collection_health.toLowerCase()] ?? 2,
    DEVICE_STATE_RANK[String(device.freshness.state).toLowerCase()] ?? 2,
    device.overlay_expected > device.overlay_ready ? 1 : 3
  );

export const buildHubDeviceDrawerViewModel = (
  datum: HubDeviceMarkerDatum
): HubDeviceDrawerViewModel => ({
  hubID: datum.hub.id,
  hubName: datum.hub.name || datum.hub.external_id || datum.hub.id,
  overall: datum.summary.overall,
  summary: datum.summary,
  devices: [...datum.devices]
    .sort(
      (left, right) =>
        deviceRank(left) - deviceRank(right) ||
        freshnessTime(left) - freshnessTime(right) ||
        left.display_name.localeCompare(right.display_name) ||
        left.id.localeCompare(right.id)
    )
    .map(device => ({
      id: device.id,
      displayName: device.display_name,
      hostname: device.hostname,
      role: device.device_role,
      reachability: device.reachability,
      collectionHealth: device.collection_health,
      overlayExpected: device.overlay_expected,
      overlayReady: device.overlay_ready,
      freshness: String(device.freshness.state),
      alertCount: device.alert_summary.firing_count,
      unacknowledgedAlertCount: device.alert_summary.unacknowledged_count
    })),
  alerts: datum.alerts
});

const categoryLabel = (category: DeviceMetricCategory) =>
  ({
    resource: 'Resource utilization',
    environment: 'Environment',
    hardware: 'Hardware',
    routing: 'Routing',
    collection: 'Collection'
  })[category];

export const buildDeviceMetricsViewModel = (
  response: DeviceMetricsResponse
): DeviceMetricsViewModel => {
  const groupsByCategory = new Map(response.groups.map(group => [group.category, group]));
  return {
    deviceID: response.device.id,
    displayName: response.device.display_name,
    hostname: response.device.hostname,
    role: response.device.device_role,
    reachability: response.reachability,
    collectionHealth: response.collection_health,
    freshness: String(response.freshness.state),
    alertCount: response.alert_total,
    alertsTruncated: response.alerts_truncated,
    groups: DEVICE_METRIC_CATEGORIES.map(category => {
      const group = groupsByCategory.get(category);
      return {
        category,
        label: categoryLabel(category),
        availability: group?.availability ?? 'unavailable',
        observedAt: group?.observed_at,
        metrics: group?.metrics ?? [],
        returnedCount: group?.returned_count ?? 0,
        totalCount: group?.total_count ?? 0,
        truncated: group?.truncated ?? false,
        diagnostic: group?.diagnostic ?? (group ? undefined : 'No current group data was returned.')
      };
    })
  };
};
